/**
 * DOM helpers for the tour overlay and auto-start holds. Kept apart from the React code so the
 * geometry and the dialog-hold rule are unit testable on their own.
 */
import {
  VIEWPORT_MARGIN,
  computeAnchoredPosition,
  type AnchorRect,
  type PanelPosition,
} from "../../utils/anchoredPosition";
import type { TourStep } from "../../types/tours";
import {
  canAutoPressAnchor,
  COMMIT_CLOSE_ANCHORS,
  MODAL_CLOSE_ANCHORS,
  MODAL_OPENER_ANCHORS,
  POPUP_CLOSE_ANCHORS,
  SKIP_WHEN_ABSENT_ANCHORS,
  TOUR_ANCHOR_ATTRIBUTE,
} from "./tourAnchors";
import { isPopupOpenerStep, stepInteraction, type TourRun } from "./tourEngine";

/** Marks the overlay root so the hold and inert logic can tell it apart from host dialogs. */
export const TOUR_OVERLAY_ATTRIBUTE = "data-tour-overlay";

/**
 * Marks an element the tour itself made inert, so the tour's own shield can be told apart from a
 * panel that marks itself closed with `inert`.
 *
 * Without it, everything the tour has shielded looks shut: `openHostDialogs` ignores an `inert`
 * subtree, and while a plain step is on screen every child of `body` is inert — including the
 * modal the coach is standing inside. Asked while a tour is on screen, that reads as "no dialog is
 * open" at exactly the moment one is.
 */
export const TOUR_INERT_ATTRIBUTE = "data-tour-inert";

const isRendered = (element: Element): boolean => {
  const rect = element.getBoundingClientRect();
  return rect.width > 0 || rect.height > 0;
};

/**
 * The first visible element tagged with `anchorId`, falling back to the first match at all (jsdom
 * and a still-laying-out page both report zero rects). Null when nothing carries the anchor.
 *
 * An anchor inside an `aria-hidden="true"` subtree does not count, for the same reason
 * `openHostDialogs` ignores one: a slide panel that stays mounted while closed (`ManageTeamsPanel`)
 * still has a full-height box, parked off screen by a transform. Without this it measures as a
 * perfectly good rect and the spotlight lands on empty space at the edge of the viewport, rather
 * than on the centered card a step whose element is not on screen is supposed to get.
 *
 * `inert` is deliberately *not* treated the same way: the overlay inerts every other child of
 * `body` while a tour is up, so every anchor on the page is inside an inert subtree for the whole
 * of the run.
 */
/** Selector for everything tagged with `anchorId`, with the id escaped for an attribute value. */
export const anchorSelector = (anchorId: string): string =>
  `[${TOUR_ANCHOR_ATTRIBUTE}="${anchorId.replace(/["\\]/g, "\\$&")}"]`;

export const findAnchorElement = (
  anchorId: string,
  root: ParentNode = document
): HTMLElement | null => {
  const matches = Array.from(
    root.querySelectorAll<HTMLElement>(anchorSelector(anchorId))
  ).filter(
    (element) =>
      !element.closest(`[${TOUR_OVERLAY_ATTRIBUTE}]`) &&
      !element.closest('[aria-hidden="true"]')
  );
  return matches.find(isRendered) ?? matches[0] ?? null;
};

/**
 * Whether everything tagged with `anchorId` sits inside a closed panel (an element with the `hidden` attribute), so
 * none of it can be seen until the coach opens that panel. False when nothing carries the anchor: that is
 * `SKIP_WHEN_ABSENT_ANCHORS`'s question, not this one. Read from the attribute rather than a measured box, so it
 * answers the same in jsdom and before layout.
 */
export const isAnchorFolded = (anchorId: string, root: ParentNode = document): boolean => {
  const matches = Array.from(root.querySelectorAll<HTMLElement>(anchorSelector(anchorId))).filter(
    (element) => !element.closest(`[${TOUR_OVERLAY_ATTRIBUTE}]`)
  );
  return matches.length > 0 && matches.every((element) => element.closest("[hidden]") !== null);
};

/**
 * The anchor element only when it actually has a box.
 *
 * `findAnchorElement` deliberately falls back to the first match with no rect, because a page that
 * has not laid out yet still has the right element. Anywhere that is about to *do* something to the
 * element — focus it, or count it as a way forward — needs the stricter test instead: a
 * `display: none` control cannot take focus, so admitting it to the tour's Tab ring would put a
 * dead stop where a keyboard user's only route to Skip is.
 */
export const findRenderedAnchorElement = (
  anchorId: string,
  root: ParentNode = document
): HTMLElement | null => {
  const element = findAnchorElement(anchorId, root);
  return element && isRendered(element) ? element : null;
};

/**
 * The longest control name the bubble will name in its instruction.
 *
 * The point of naming the control is that "Click Columns to continue" is one glance. A row anchor's
 * text content can be a whole table row, and pasting that into the bold line rebuilds the paragraph
 * this replaced — so anything past a short button's worth of words reads as no usable name at all
 * and the bubble falls back to "the highlighted control".
 */
const MAX_CONTROL_NAME_LENGTH = 32;

/**
 * What to call the control a `ClickToContinue` step is asking for, taken from the element itself.
 *
 * `aria-label` first, because an icon-only control (the tags Close button) has no text worth
 * reading, then the trimmed text content. Null when neither gives a short, usable name, and the
 * bubble says "the highlighted control" instead — the sentence has to read either way.
 *
 * Whitespace is collapsed: JSX indents its children, so a button's `textContent` arrives with
 * newlines in it.
 */
export const anchorControlName = (element: HTMLElement | null): string | null => {
  if (!element) return null;
  const candidates = [element.getAttribute("aria-label"), element.textContent];
  for (const candidate of candidates) {
    const name = candidate?.replace(/\s+/g, " ").trim() ?? "";
    if (name && name.length <= MAX_CONTROL_NAME_LENGTH) return name;
  }
  return null;
};

/**
 * Open `aria-modal` dialogs that are not the tour's own bubble.
 *
 * A slide panel that stays mounted while closed (for its transition) keeps `aria-modal="true"` and
 * marks itself closed with `inert` or `aria-hidden`. Counting it as open held the Players page tour
 * forever, so anything inside an inert or hidden subtree is not an open dialog.
 */
export const openHostDialogs = (
  root: ParentNode = document,
  /**
   * Ignore the `inert` the tour itself applied. Auto-start asks with this off, because no tour is
   * on screen to have shielded anything; anything asking while a tour is up needs it on.
   */
  { ignoreTourInert = false }: { ignoreTourInert?: boolean } = {}
): HTMLElement[] => {
  const closed = ignoreTourInert
    ? `[aria-hidden="true"], [inert]:not([${TOUR_INERT_ATTRIBUTE}])`
    : '[inert], [aria-hidden="true"]';
  return Array.from(root.querySelectorAll<HTMLElement>('[aria-modal="true"]')).filter(
    (dialog) => !dialog.closest(`[${TOUR_OVERLAY_ATTRIBUTE}]`) && !dialog.closest(closed)
  );
};

/**
 * Whether an open dialog should hold this run back.
 *
 * A tour waits for every dialog to close: spotlighting the page behind a modal points at controls
 * the user cannot reach. The exception is a tour whose first anchored step sits inside that dialog,
 * because then the dialog is what it explains. That is a flow-step tour over its modal, or a page
 * tour for a view that is itself a dialog (the schedule's itinerary catalog). A data tour never
 * qualifies: its rows live on the page behind.
 */
export const isHeldByOpenDialog = (run: TourRun, root: ParentNode = document): boolean => {
  const dialogs = openHostDialogs(root);
  if (dialogs.length === 0) return false;
  if (run.tour.kind === "Data") return true;

  const firstAnchored = run.steps.find((step) => step.anchor !== null);
  const anchor = firstAnchored?.anchor ? findAnchorElement(firstAnchored.anchor, root) : null;
  if (!anchor) return true;
  return !dialogs.every((dialog) => dialog.contains(anchor));
};

/**
 * How deep we are inside a press the tour made itself, rather than the coach making it.
 *
 * `element.click()` dispatches synchronously, so a counter around the call is enough for any
 * listener that fires during it to ask who pressed. `ClickToContinue` is the listener that has to
 * ask: it matches on the anchor selector alone, so the end-of-run tidy-up pressing a control that
 * a step of the same run is watching is indistinguishable from the coach pressing it — and the
 * result is a second progress record, with the wrong outcome. No anchor in today's manifest is
 * used by two steps of one tour, but steps are authored in the API and an admin can write exactly
 * that.
 *
 * `event.isTrusted` would answer the same question in a browser and nowhere else: jsdom reports
 * `false` for every event a test fires, so every interactive step in the suite would read as the
 * tour pressing its own control.
 */
let tourPressDepth = 0;

/** Whether the click being handled right now is one the tour made on the coach's behalf. */
export const isTourInitiatedPress = (): boolean => tourPressDepth > 0;

/**
 * Presses `element` as the tour rather than as the coach, so the tour's own listeners can tell the
 * two apart. Every programmatic press the tour makes goes through here.
 */
export const pressAnchorElement = (element: HTMLElement): void => {
  tourPressDepth += 1;
  try {
    element.click();
  } finally {
    tourPressDepth -= 1;
  }
};

/**
 * Closes what this run opened, once, as it ends.
 *
 * A tour that ends on a detour leaves the popup it asked the coach to open sitting there — and
 * when the page tour chains into the data tour, that popup is inerted behind the next run's shade
 * with no way to reach it: `ColumnSettingsPanel` has no close control, its outside-press dismissal
 * never fires because the overlay swallows `mousedown`, and Escape on a plain step is the tour's.
 *
 * Bounded three ways, so the tour can never press something it did not open: the step's click has
 * to be one this run recorded (a coach's press on a `ClickToContinue` control, or the tour's own
 * `OpenAnchor` press), the anchor has to be one the tour was allowed to press in the first place
 * (`AUTO_PRESS_ANCHORS`), and the control has to still say it is open. A control with no
 * `aria-expanded` is left alone rather than guessed at — a close button carries no such flag, and
 * pressing one of those is how the tidy-up would start closing things on its own.
 *
 * Returns the anchors it pressed, for the caller's tests to name.
 */
export const closeAnchorsOpenedByRun = (
  steps: readonly TourStep[],
  satisfiedStepKeys: readonly string[],
  root: ParentNode = document,
  { closeModals = true }: { closeModals?: boolean } = {}
): string[] => {
  // No next step: the run is leaving, so every popup it opened goes.
  const pressed = closePopupsLeftBehind(steps, satisfiedStepKeys, null, root);
  const seen = new Set<string>();
  for (const step of steps) {
    const anchorId = step.anchor;
    if (anchorId === null || seen.has(anchorId)) continue;
    if (!satisfiedStepKeys.includes(step.key) || !canAutoPressAnchor(anchorId)) continue;
    seen.add(anchorId);
    // A popup whose opener publishes no `aria-expanded` is closed through its own close button,
    // by the `closePopupsLeftBehind` call at the top of this function.
    if (POPUP_CLOSE_ANCHORS.has(anchorId)) continue;
    // A modal opener only ever opens: pressed again it would not close the modal it reports open.
    // The modal is closed through its own close button instead, never its Save or Attach.
    // `closeModals: false` leaves it, with the coach's input, when nothing takes the screen next.
    if (MODAL_OPENER_ANCHORS.has(anchorId)) {
      if (closeModals && closeModalOpenedBy(anchorId, root)) {
        pressed.push(MODAL_CLOSE_ANCHORS.get(anchorId)!);
      }
      continue;
    }
    const element = findRenderedAnchorElement(anchorId, root);
    if (!element || element.getAttribute("aria-expanded") !== "true") continue;
    // Marked as the tour's own press: a `ClickToContinue` step still watching this same anchor
    // must not read the tidy-up as the coach working the control.
    pressAnchorElement(element);
    pressed.push(anchorId);
  }
  return pressed;
};

/**
 * Closes a popup this run opened when the tour moves on to a step that is not inside it.
 *
 * The manifest no longer spends a step on "Close it when you are done": the coach presses Next on
 * the last step inside the tag manager and the tour closes it for them, so the step after it
 * points at a page that is not covered by a modal. Only popups listed in `POPUP_CLOSE_ANCHORS`
 * are closed, both sides of the pair must be in `AUTO_PRESS_ANCHORS`, the opener's press must be
 * in this run's record, and the close button must sit inside a `role="dialog"`.
 *
 * "Not inside it" is decided only from what is on the page. When the next step's anchor cannot be
 * found at all, the popup stays open: the anchor may not have rendered yet (the tag manager draws
 * its categories after they load, and none at all once an org has the maximum), and closing then
 * would leave that step pointing at nothing. The end of the run closes it instead, which is the
 * `nextStep = null` call from `closeAnchorsOpenedByRun`. A next step with no anchor at all is a
 * centered card over the page, so the popup goes.
 *
 * Returns the close anchors it pressed, for the caller's tests to name.
 */
export const closePopupsLeftBehind = (
  steps: readonly TourStep[],
  satisfiedStepKeys: readonly string[],
  nextStep: TourStep | null,
  root: ParentNode = document
): string[] => {
  const pressed: string[] = [];
  for (const step of steps) {
    const openerId = step.anchor;
    if (openerId === null || !satisfiedStepKeys.includes(step.key)) continue;
    const closeId = POPUP_CLOSE_ANCHORS.get(openerId);
    if (!closeId || pressed.includes(closeId)) continue;
    if (!canAutoPressAnchor(openerId) || !canAutoPressAnchor(closeId)) continue;
    const close = findRenderedAnchorElement(closeId, root);
    const popup = close?.closest('[role="dialog"]');
    if (!close || !popup) continue;
    if (nextStep?.anchor) {
      const nextAnchor = findAnchorElement(nextStep.anchor, root);
      if (!nextAnchor || popup.contains(nextAnchor)) continue;
    }
    pressAnchorElement(close);
    pressed.push(closeId);
  }
  return pressed;
};

/**
 * Closes the modal `openerId` opened, through that modal's own close button
 * (`MODAL_CLOSE_ANCHORS`). Only while the opener still reports the modal open, only when the close
 * button sits inside a `role="dialog"`, and never while the button is disabled: the modals disable
 * it during a save, and a save that is landing must be left to land. Returns whether it pressed.
 */
export const closeModalOpenedBy = (openerId: string, root: ParentNode = document): boolean => {
  const closeId = MODAL_CLOSE_ANCHORS.get(openerId);
  if (!closeId) return false;
  const opener = findRenderedAnchorElement(openerId, root);
  if (opener?.getAttribute("aria-expanded") !== "true") return false;
  const close = findRenderedAnchorElement(closeId, root);
  if (!close?.closest('[role="dialog"]') || isDisabledControl(close)) return false;
  pressAnchorElement(close);
  return true;
};

const isDisabledControl = (element: HTMLElement): boolean =>
  (element as HTMLButtonElement).disabled === true ||
  element.getAttribute("aria-disabled") === "true";

/**
 * Whether a commit step's save is in flight: its commit button is still on the page and the
 * modal's close button is disabled, which is how the board modals hold the coach while Attach or
 * Save runs. Next waits then rather than moving on behind a modal it cannot close, and the armed
 * step advances by itself once the save lands and the modal closes.
 */
export const isCommitInFlight = (
  step: TourStep | null | undefined,
  root: ParentNode = document
): boolean => {
  if (!step?.anchor || stepInteraction(step) !== "ClickToContinue") return false;
  const closeId = COMMIT_CLOSE_ANCHORS.get(step.anchor);
  if (!closeId || !findRenderedAnchorElement(step.anchor, root)) return false;
  const close = findRenderedAnchorElement(closeId, root);
  return close !== null && isDisabledControl(close);
};

/**
 * Next on a commit step (Attach, Save) whose modal is still open: closes that modal through its own
 * close button (`COMMIT_CLOSE_ANCHORS`), so the coach moves on without saving and the next step's
 * modal never opens on top of this one. The commit button itself is never pressed.
 *
 * Only for a `ClickToContinue` step on a commit button, only while that button is on the page, and
 * only when the close button sits inside a `role="dialog"`. Returns the close anchor it pressed, or
 * null.
 */
export const closeCommitModal = (
  step: TourStep | null | undefined,
  root: ParentNode = document
): string | null => {
  if (!step?.anchor || stepInteraction(step) !== "ClickToContinue") return null;
  const closeId = COMMIT_CLOSE_ANCHORS.get(step.anchor);
  if (!closeId || !findRenderedAnchorElement(step.anchor, root)) return null;
  const close = findRenderedAnchorElement(closeId, root);
  if (!close?.closest('[role="dialog"]')) return null;
  pressAnchorElement(close);
  return closeId;
};

/*
 * Orphaned sub-steps.
 *
 * A popup's steps follow the step that opens it (the board tour's pencil, then the player editor's
 * fields). When that opener's control is not on the page at all, an empty board having no rows to
 * put a pencil on, the popup can never open, and its steps would each show as a centered card
 * describing a form the coach cannot see. Those steps are skipped.
 *
 * The rule needs nothing in the manifest. A step is an orphan when its own anchor is not on the
 * page, and walking back through steps whose anchors are not on the page either reaches an opener
 * (a `ClickToContinue` step on an `AUTO_PRESS_ANCHORS` control) that is missing too. A step whose
 * anchor is present, a centered card, or a present opener ends the walk, so a popup that is merely
 * slow to mount (the opener is still on the page) keeps its steps.
 */
export const isOrphanedSubStep = (
  steps: readonly TourStep[],
  index: number,
  root: ParentNode = document
): boolean => {
  const step = steps[index];
  if (!step?.anchor || findAnchorElement(step.anchor, root)) return false;
  for (let earlier = index - 1; earlier >= 0; earlier -= 1) {
    const candidate = steps[earlier];
    if (candidate.anchor === null || findAnchorElement(candidate.anchor, root)) return false;
    if (isPopupOpenerStep(candidate)) return true;
  }
  return false;
};

/**
 * The step moving forward from `fromIndex` lands on, past any orphaned sub-steps. Can return
 * `steps.length`, meaning the run has nothing left to show.
 *
 * Forward only. Back cannot ask the page: a popup the coach moved past is closed by then, so its
 * steps look orphaned exactly when Back is about to re-open it. The provider remembers which steps
 * forward skipped and Back passes over only those.
 */
export const shownStepIndex = (
  steps: readonly TourStep[],
  fromIndex: number,
  root: ParentNode = document
): number => {
  let index = fromIndex + 1;
  while (
    index < steps.length &&
    (isOrphanedSubStep(steps, index, root) || isAbsentRecordStep(steps[index], root))
  ) {
    index += 1;
  }
  return index;
};

/**
 * A step about a record the page may not have (`SKIP_WHEN_ABSENT_ANCHORS`) whose record is not on
 * the page as the run reaches it. Only a step inside a popup the run opens gets this far
 * (`withoutAbsentRecordSteps` leaves out the others as the run starts): the Schools tab's roster
 * value, drawn for a college program's ranked roster only. It is passed over rather than shown as a
 * card about a number the coach cannot see, and the run still records it as seen.
 */
const isAbsentRecordStep = (step: TourStep, root: ParentNode): boolean =>
  step.anchor !== null &&
  SKIP_WHEN_ABSENT_ANCHORS.has(step.anchor) &&
  findAnchorElement(step.anchor, root) === null;

/*
 * Back.
 *
 * Back always lands on the previous step, and puts the page back the way that step was shown. The
 * rule it replaced tried to walk past every step that "could not be shown right now" and land on
 * the nearest one that could. In the Players walkthrough that meant every step inside the filter
 * modal or the tag manager looked unshowable as soon as the coach had moved past it (Customize Tags
 * swaps the filter modal for the tag manager, and the step after the tag manager closes it), so
 * Back from step 11 or 13 of 14 jumped to step 8, and from inside the modal to step 7.
 *
 * What a step needs is which popups the run had open while it was on screen. The provider records
 * that as the coach leaves each step (`openPopupOpenerKeys`), and Back re-creates it: if the popups
 * open now are not the ones the step had, it closes what the run opened and presses the openers
 * again, in order (`openerPressPlan`, `pressOpenersInOrder`). The landing step and everything after
 * it are re-armed, so a step that asked for a click asks for it again.
 */

/**
 * Whether the popup `openerId` shows is open right now, for the openers the tour can tell about:
 * one paired with a close button in `POPUP_CLOSE_ANCHORS` (open while that button is on the page
 * inside a dialog), or one that publishes `aria-expanded`. Anything else reads as not open, which
 * leaves it out of the record and so out of anything Back would press.
 */
export const isPopupOpen = (openerId: string, root: ParentNode = document): boolean => {
  if (!canAutoPressAnchor(openerId)) return false;
  const closeId = POPUP_CLOSE_ANCHORS.get(openerId);
  if (closeId !== undefined) {
    const close = findRenderedAnchorElement(closeId, root);
    return close !== null && close.closest('[role="dialog"]') !== null;
  }
  const opener = findRenderedAnchorElement(openerId, root);
  return opener?.getAttribute("aria-expanded") === "true";
};

/**
 * The steps of this run whose press has opened a popup that is still open, in step order, one per
 * opener. `excludeStepKey` leaves out the step being recorded: its own press belongs to the step
 * after it, and Back re-arms it rather than restoring it.
 */
export const openPopupOpenerKeys = (
  steps: readonly TourStep[],
  satisfiedStepKeys: readonly string[],
  excludeStepKey: string | null,
  root: ParentNode = document
): string[] => {
  const seen = new Set<string>();
  const keys: string[] = [];
  for (const step of steps) {
    if (step.anchor === null || step.key === excludeStepKey) continue;
    if (!satisfiedStepKeys.includes(step.key) || seen.has(step.anchor)) continue;
    if (!isPopupOpen(step.anchor, root)) continue;
    seen.add(step.anchor);
    keys.push(step.key);
  }
  return keys;
};

/**
 * The openers to press, in order, to get from nothing open to `desired` open.
 *
 * An opener can live inside another popup (Customize Tags sits in the filter modal), so each one is
 * preceded by whatever was open when it was pressed, from the same record. That is how Back from
 * Columns to the last tag manager step opens Filter, then Customize Tags.
 */
export const openerPressPlan = (
  desired: readonly string[],
  openersByStep: ReadonlyMap<string, readonly string[]>
): string[] => {
  const plan: string[] = [];
  const visiting = new Set<string>();
  const visit = (stepKey: string) => {
    if (plan.includes(stepKey) || visiting.has(stepKey)) return;
    visiting.add(stepKey);
    (openersByStep.get(stepKey) ?? []).forEach(visit);
    plan.push(stepKey);
  };
  desired.forEach(visit);
  return plan;
};

/** How long each press in a restore waits for its control, or for its popup, before moving on. */
const RESTORE_MAX_FRAMES = 30;

/**
 * Presses each opener in turn, a frame apart, waiting for its control to render and then for its
 * popup to open before the next. A popup mounts on the render after the press, and an opener inside
 * a popup (Customize Tags) only exists once that popup does, so the presses cannot go in one tick.
 *
 * Only `AUTO_PRESS_ANCHORS` are pressed, an opener whose popup is already open is skipped (a second
 * press would close a toggle), and a control that never turns up is given up on rather than waited
 * for. `onDone` is called once the last opener is dealt with, and never after a cancel. Returns the
 * cancel function.
 */
export const pressOpenersInOrder = (
  anchorIds: readonly string[],
  root: ParentNode = document,
  onDone?: () => void
): (() => void) => {
  let index = 0;
  let pressed = false;
  let frames = 0;
  let cancelFrame: (() => void) | null = null;

  const tick = () => {
    cancelFrame = null;
    const anchorId = anchorIds[index];
    if (anchorId === undefined) return;
    frames += 1;
    const advance = () => {
      index += 1;
      pressed = false;
      frames = 0;
    };

    if (!canAutoPressAnchor(anchorId)) {
      advance();
    } else if (!pressed) {
      if (isPopupOpen(anchorId, root)) {
        advance();
      } else {
        const element = findRenderedAnchorElement(anchorId, root);
        if (element) {
          pressAnchorElement(element);
          pressed = true;
          frames = 0;
        } else if (frames >= RESTORE_MAX_FRAMES) {
          advance();
        }
      }
    } else if (isPopupOpen(anchorId, root) || frames >= RESTORE_MAX_FRAMES) {
      advance();
    }

    if (index < anchorIds.length) cancelFrame = scheduleFrame(tick);
    else onDone?.();
  };

  if (anchorIds.length === 0) {
    onDone?.();
    return () => {};
  }
  cancelFrame = scheduleFrame(tick);
  return () => {
    cancelFrame?.();
    cancelFrame = null;
  };
};

export interface BubblePlacementInput {
  anchor: AnchorRect;
  bubbleWidth: number;
  bubbleHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  gap: number;
}

/**
 * Below or above the anchor when either side fits, otherwise beside it, otherwise the clamped
 * vertical placement `usePopoverPosition` uses. Never off screen.
 */
export const placeTourBubble = ({
  anchor,
  bubbleWidth,
  bubbleHeight,
  viewportWidth,
  viewportHeight,
  gap,
}: BubblePlacementInput): PanelPosition => {
  const vertical = () =>
    computeAnchoredPosition({
      anchor,
      panelWidth: bubbleWidth,
      panelHeight: bubbleHeight,
      viewportWidth,
      viewportHeight,
      gap,
    });

  const fitsBelow = viewportHeight - anchor.bottom - gap - VIEWPORT_MARGIN >= bubbleHeight;
  const fitsAbove = anchor.top - gap - VIEWPORT_MARGIN >= bubbleHeight;
  if (fitsBelow || fitsAbove) return vertical();

  const clampedTop = Math.min(
    Math.max(VIEWPORT_MARGIN, anchor.top),
    Math.max(VIEWPORT_MARGIN, viewportHeight - bubbleHeight - VIEWPORT_MARGIN)
  );
  if (viewportWidth - anchor.right - gap - VIEWPORT_MARGIN >= bubbleWidth) {
    return { top: clampedTop, left: anchor.right + gap };
  }
  if (anchor.left - gap - VIEWPORT_MARGIN >= bubbleWidth) {
    return { top: clampedTop, left: anchor.left - gap - bubbleWidth };
  }
  return vertical();
};

/**
 * Sets `inert` on every direct child of `body` except `keep`, including children added later, and
 * returns a function that restores exactly what it changed. An element that was already inert stays
 * inert afterwards.
 */
export const inertSiblings = (keep: Element): (() => void) => {
  const changed = new Set<Element>();
  const apply = (element: Element) => {
    if (element === keep || element.hasAttribute("inert")) return;
    element.setAttribute("inert", "");
    // Says who inerted it: see TOUR_INERT_ATTRIBUTE. Removed again with the inert it belongs to.
    element.setAttribute(TOUR_INERT_ATTRIBUTE, "");
    changed.add(element);
  };

  Array.from(document.body.children).forEach(apply);

  const observer =
    typeof MutationObserver === "undefined"
      ? null
      : new MutationObserver((records) => {
          records.forEach((record) =>
            record.addedNodes.forEach((node) => {
              if (node instanceof Element && node.parentElement === document.body) apply(node);
            })
          );
        });
  observer?.observe(document.body, { childList: true });

  return () => {
    observer?.disconnect();
    changed.forEach((element) => {
      element.removeAttribute("inert");
      element.removeAttribute(TOUR_INERT_ATTRIBUTE);
    });
    changed.clear();
  };
};

export const prefersReducedMotion = (): boolean => {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  } catch {
    return false;
  }
};

/** Next animation frame, with a timer fallback for environments that have no rAF. */
export const scheduleFrame = (callback: () => void): (() => void) => {
  if (typeof requestAnimationFrame === "function") {
    const frame = requestAnimationFrame(callback);
    return () => cancelAnimationFrame(frame);
  }
  const timer = setTimeout(callback, 16);
  return () => clearTimeout(timer);
};
